import React, { useEffect, useState } from 'react';
import { X, ZoomIn } from 'lucide-react';
import { galleryImages } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicContent, fetchPublicSettings, makeSectionCopyPicker } from '../services/publicContentService';

const GALLERY_ICON =
  'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/1zccfhot_icon1.png';

const Gallery = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [selectedImage, setSelectedImage] = useState(null);

  const [images, setImages] = useState(galleryImages);
  const [sc, setSc] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPublicContent('gallery').then((items) => {
      if (!alive || !Array.isArray(items) || items.length === 0) return;
      const mapped = items
        .filter((it) => !!it.image)
        .map((it) => ({
          id: it.id,
          url: it.image,
          title:   it.title_ar || it.title_en || '',
          titleEn: it.title_en || it.title_ar || '',
        }));
      if (mapped.length > 0) setImages(mapped);
    });
    fetchPublicSettings('section_copy').then((v) => { if (alive) setSc(v || {}); });
    return () => { alive = false; };
  }, []);
  const sec = makeSectionCopyPicker(sc, isAr);

  return (
    <section id="gallery" className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-teal-50/50 to-cyan-50/50 dark:from-slate-900 dark:to-slate-800" />

      <div className="relative container mx-auto px-4">
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-flex items-center gap-3 mb-4">
            <h2 data-testid="gallery-heading" className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white">
              {sec('gallery_heading', isAr ? 'معرض المشاريع' : 'Project Gallery')}
            </h2>
          </div>
          <p data-testid="gallery-subtitle" className="text-lg text-slate-600 dark:text-slate-400">
            {sec('gallery_subtitle', isAr ? 'استكشف مشاريعنا البيئية والبحرية' : 'Explore our environmental and marine projects')}
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-6 max-w-7xl mx-auto">
        {images.map((image, index) => (
          <div
            key={image.id}
            className="relative group cursor-pointer animate-fade-in-up overflow-hidden rounded-2xl
                      w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)]"
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => setSelectedImage(image)}
            >
              <div className="relative aspect-[4/3] overflow-hidden rounded-2xl 
                            bg-gradient-to-br from-cyan-100 to-blue-100 
                            dark:from-slate-800 dark:to-slate-700">
                <img
                  src={image.url}
                  alt={isAr ? image.title : (image.titleEn || image.title)}
                  className="w-full h-full object-cover transition-all duration-700 
                           group-hover:scale-110 group-hover:rotate-2"
                  loading="lazy"
                />
                
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent 
                              opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
                
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 
                              group-hover:translate-y-0 transition-transform duration-300">
                  <h3 className={`text-lg font-bold text-white drop-shadow-lg ${isAr ? 'text-right' : 'text-left'}`}>
                    {isAr ? image.title : (image.titleEn || image.title)}
                  </h3>
                </div>

                <div className="absolute inset-0 flex items-center justify-center 
                              opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                    <ZoomIn className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>

              <div className="absolute inset-0 rounded-2xl ring-2 ring-transparent 
                            group-hover:ring-cyan-400 dark:group-hover:ring-cyan-600 
                            transition-all duration-300" />
            </div>
          ))}
        </div>
      </div>

      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm 
                   animate-fade-in p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 
                     transition-colors duration-200"
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-6 h-6 text-white" />
          </button>

          <div className="relative max-w-6xl max-h-[90vh] animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.url}
              alt={isAr ? selectedImage.title : (selectedImage.titleEn || selectedImage.title)}
              className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl"
            />
            <div className="mt-4 text-center">
              <h3 className="text-2xl font-bold text-white drop-shadow-lg">
                {isAr ? selectedImage.title : (selectedImage.titleEn || selectedImage.title)}
              </h3>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
